import type { QueryParamConfig } from 'serialize-query-params';
import { useQueryParam } from 'use-query-params';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce, isEqual } from 'lodash-es';

interface UseDebouncedQueryParamProps<T> {
  name: string;
  defaultValue?: T;
  debounceTime?: number;
}

export const useDebouncedQueryParam = <T, D>({
  name,
  defaultValue,
  debounceTime = 700,
}: UseDebouncedQueryParamProps<T>) => {
  const [param, setParam] = useQueryParam(name, defaultValue as QueryParamConfig<D>);
  const [actualValue, setActualValue] = useState(param);
  const setDebounced = useMemo(() => debounce(setParam, debounceTime), [debounceTime, setParam]);

  // Sync actualValue when the URL changes externally (e.g. another component instance does an immediate reset).
  // Cancel any pending debounced write so it doesn't overwrite the external change.
  useEffect(() => {
    if (!isEqual(actualValue, param)) {
      setDebounced.cancel();
      setActualValue(param);
    }
  }, [param]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normal path: optimistic UI update + debounced URL write
  const handleChange = useCallback(
    (newValue: D) => {
      setActualValue(newValue);
      setDebounced(newValue);
    },
    [setDebounced]
  );

  // Immediate path: cancels any pending debounce, updates URL right away.
  const handleChangeImmediate = useCallback(
    (newValue: D) => {
      setDebounced.cancel();
      setActualValue(newValue);
      setParam(newValue);
    },
    [setDebounced, setParam]
  );

  return { param, handleChange, handleChangeImmediate, actualValue };
};
